<script setup>
import { computed, onMounted, ref } from "vue";
import { Link, router, usePage } from "@inertiajs/vue3";

import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import Dropdown from "@/Components/Dropdown.vue";
import DropdownLink from "@/Components/DropdownLink.vue";
import MaterialIcon from "../MaterialIcon.vue";
import NavSegment from "@/Components/Nav/NavSegment.vue";
import ProfileInfo from "@/Components/ProfileInfo.vue";
import ApplicationMark from "../ApplicationMark.vue";

const emits = defineEmits(["collapse:navigation"]);

// const showingNavigationDropdown = ref(false);
const collapseNavigation = ref(false);

const logout = () => {
  router.post(route("logout"));
};

const toggleCollapse = () => {
  collapseNavigation.value = !collapseNavigation.value;
  emits("collapse:navigation", collapseNavigation.value);
};

const page = usePage();
const canRead = computed(() => page.props.canRead);

onMounted(() => {
  // check if width of window is less than 640px
  if (window.innerWidth < 1000) {
    collapseNavigation.value = true;
    emits("collapse:navigation", collapseNavigation.value);
  }
});
</script>

<template>
  <nav
    id="primary-navigation"
    :class="collapseNavigation ? 'collapsed' : ''"
    :data-collapsed="collapseNavigation"
  >
    <!-- Primary Navigation Menu -->
    <div class="primary-nav">
      <div>
        <!-- Application Logo & Name -->
        <div class="application-label">
          <!-- Logo -->
          <div>
            <Link :href="route('dashboard')">
              <ApplicationLogo v-if="!collapseNavigation" />
              <ApplicationMark v-else />
            </Link>
          </div>

          <!-- Navigation Links -->
          <div class="label">
            <Link
              :href="route('dashboard')"
              :active="route().current('dashboard')"
            >
            </Link>
          </div>
        </div>

        <div class="main-nav" v-if="canRead">
          <NavSegment
            :collapse="collapseNavigation"
            title="Dashboard"
            short="DB"
            :menu_items="[
              {
                text: 'Dashboard',
                icon_name: 'home',
                href: route('dashboard'),
              },
              {
                text: 'Trades',
                icon_name: 'swap_horiz',
                href: route('trading_transactions.index'),
              },
              ...(canRead.deliveries
                ? [
                    {
                      text: 'Deliveries',
                      icon_name: 'train',
                      href: route('deliveries.index'),
                    },
                  ]
                : []),
            ]"
          />
          <NavSegment
            :collapse="collapseNavigation"
            title="Finance & Billing"
            short="FB"
            :menu_items="[
              ...(canRead.invoices
                ? [
                    {
                      text: 'Purchase Invoices',
                      icon_name: 'receipt_long',
                      href: route('invoices.index', { outgoing: 0 }),
                    },
                    {
                      text: 'Sales Invoices',
                      icon_name: 'receipt_long',
                      href: route('invoices.index', { outgoing: 1 }),
                    },
                  ]
                : []),
            ]"
          />
          <NavSegment
            :collapse="collapseNavigation"
            title="Analytics & Reports"
            short="AR"
            :menu_items="[
              ...(canRead.reports
                ? [
                    {
                      text: 'Reports',
                      icon_name: 'bar_chart',
                      href: route('reports.index'),
                    },
                  ]
                : []),
            ]"
          />
          <NavSegment
            :collapse="collapseNavigation"
            title="Products & Pricing"
            short="PP"
            :menu_items="[
              ...(canRead.products
                ? [
                    {
                      text: 'Products',
                      icon_name: 'inventory_2',
                      href: route('products.index'),
                    },
                  ]
                : []),
              ...(canRead.spot_prices
                ? [
                    {
                      text: 'Spot Prices',
                      icon_name: 'show_chart',
                      href: route('spot_prices.index'),
                    },
                  ]
                : []),
              ...(canRead.exchange_rates
                ? [
                    {
                      text: 'Exchange Rates',
                      icon_name: 'currency_exchange',
                      href: route('exchange_rates.index'),
                    },
                  ]
                : []),
            ]"
          />
          <NavSegment
            :collapse="collapseNavigation"
            title="Business Partners"
            short="BP"
            :menu_items="[
              ...(canRead.business_partners
                ? [
                    {
                      text: 'Business Partners',
                      icon_name: 'business_center',
                      href: route('business_partners.index'),
                    },
                  ]
                : []),
            ]"
          />
          <NavSegment
            :collapse="collapseNavigation"
            title="Documentation"
            short="DO"
            :menu_items="[
              ...(canRead.company_documents
                ? [
                    {
                      text: 'Company Documents',
                      icon_name: 'folder',
                      href: route('company_documents.index'),
                      children: [
                        ...(canRead.document_types
                          ? [
                              {
                                text: 'Document Types',
                                icon_name: 'tag',
                                href: route('document_types.index'),
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),

              ...(canRead.contracts
                ? [
                    {
                      text: 'Contracts',
                      icon_name: 'handshake',
                      href: route('contracts.index'),
                      children: [
                        ...(canRead.contract_templates
                          ? [
                              {
                                text: 'Contract Templates',
                                icon_name: 'description',
                                href: route('contract_templates.index'),
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
            ]"
          />
          <NavSegment
            :collapse="collapseNavigation"
            title="User Management"
            short="UM"
            :menu_items="[
              ...(canRead.users
                ? [
                    {
                      text: 'Users',
                      icon_name: 'person',
                      href: route('users.index'),
                    },
                  ]
                : []),
              ...(canRead.roles
                ? [
                    {
                      text: 'Roles',
                      icon_name: 'group',
                      href: route('roles.index'),
                    },
                  ]
                : []),
              ...(canRead.permissions
                ? [
                    {
                      text: 'Permissions',
                      icon_name: 'shield',
                      href: route('permissions.index'),
                    },
                  ]
                : []),
            ]"
          />
          <NavSegment
            :collapse="collapseNavigation"
            title="Project Settings"
            short="PS"
            :menu_items="[
              ...(canRead.settings
                ? [
                    {
                      text: 'Charge Types',
                      icon_name: 'tag',
                      href: route('charge_types.index'),
                    },
                  ]
                : []),
              ...(canRead.settings
                ? [
                    {
                      text: 'Vat Rates',
                      icon_name: 'percent',
                      href: route('vat_rates.index'),
                    },
                  ]
                : []),
              ...(canRead.backups
                ? [
                    {
                      text: 'Backups',
                      icon_name: 'backup',
                      href: route('backups.index'),
                    },
                  ]
                : []),
              ...(canRead.settings
                ? [
                    {
                      text: 'Project Settings',
                      icon_name: 'settings_applications',
                      href: route('settings.index'),
                    },
                  ]
                : []),
            ]"
          />
        </div>
      </div>

      <div>
        <!-- Settings Dropdown -->
        <div class="menu-dropdown">
          <Dropdown width="48">
            <template #trigger>
              <button v-if="page.props.jetstream?.managesProfilePhotos">
                <ProfileInfo
                  :collapse="collapseNavigation"
                  :email="page.props.auth.user.email"
                  :name="`${page.props.auth.user.first_name} ${page.props.auth.user.last_name}`"
                  :image_src="page.props.auth.user.profile_photo_url"
                  :image_alt="`${page.props.auth.user.first_name} ${page.props.auth.user.last_name}`"
                />
              </button>
            </template>

            <template #content>
              <!-- Authentication -->
              <form @submit.prevent="logout">
                <DropdownLink as="button"> Log Out </DropdownLink>
              </form>

              <div class="border-line" />

              <!-- Account Management -->
              <div class="subtile-label">Manage Account</div>

              <DropdownLink :href="route('profile.show')">
                Profile
              </DropdownLink>
              <div class="border-line" />
            </template>
          </Dropdown>
        </div>
      </div>
    </div>

    <button
      class="collapse-btn"
      :class="collapseNavigation ? 'collapsed' : ''"
      @click="toggleCollapse"
    >
      <span class="sr-only">Collapse Menu</span>
      <MaterialIcon v-if="!collapseNavigation" icon_name="navigate_before" />
      <MaterialIcon v-else icon_name="navigate_next" />
    </button>
  </nav>
</template>

<style lang="scss" scoped>
nav {
  position: fixed;
  top: 0;

  background-color: var(--clr-bg-secondary);
  border-right: solid 1px var(--clr-border-subtile);
  height: 100vh;
  min-width: 300px;

  z-index: 200;

  &.collapsed {
    min-width: unset;

    .primary-nav {
      padding: 2.5rem 1rem;
    }

    & > div:first-of-type .application-label .label {
      display: none !important;
    }
  }

  .primary-nav {
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    height: 100%;

    padding: 2.5rem 2rem;

    overflow-y: auto;
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; /* Firefox */

    &::-webkit-scrollbar {
      display: none;
    }

    & > div:first-of-type {
      display: flex;
      flex-direction: column;
      margin-bottom: 3rem;

      .application-label {
        align-items: center;
        display: flex;

        & > * + * {
          margin-left: 1rem;
        }

        .label {
          display: none;

          @media (min-width: $bp-sm) {
            display: block;
          }
        }
      }
    }
    & > div:last-of-type {
      display: none;
      justify-self: flex-end;

      @media (min-width: $bp-sm) {
        display: block;
      }

      .menu-dropdown {
        position: relative;

        .border-line {
          border-top: solid 1px var(--clr-border-subtile);
        }

        .subtile-label {
          @include ff-text;
          @include font-clr-text-subtile;

          display: block;
          padding: 0.5rem 1rem;
        }
      }
    }
  }
}

.collapse-btn {
  @include box-shadow-sm;
  --size: 2.5rem;
  width: var(--size);
  height: var(--size);
  border-radius: calc(var(--size) / 2);
  border: 1px solid
    color-mix(in srgb, var(--clr-border-subtile) 20%, transparent);

  background-color: var(--clr-bg-secondary);

  position: absolute;
  top: 4.5rem;
  right: 0;
  transform: translateX(50%);

  align-items: center;
  display: flex;
  justify-content: center;

  transition: background-color 200ms ease-in;

  &.collapsed {
    --size: 2rem;
    top: 5rem;
  }

  &:hover,
  &:focus-visible {
    background-color: var(--clr-bg-menu-accent);
  }
}
</style>
